<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useDashboardStats } from '@/composables/useDashboardStats';
import type { IManageOrganisation } from '@/types/manageOrgCommerce.types';
import { useI18n } from 'vue-i18n';

const router = useRouter();
const { orgs, incompleteOrgs, taskCount, isPending } = useDashboardStats();
const { t } = useI18n({ useScope: 'global' });

// ── Champs manquants ──────────────────────────────────────────────────────────
interface MissingField {
  field: string;
  icon: string;
}

function getMissingFields(org: IManageOrganisation): MissingField[] {
  const missing: MissingField[] = [];
  if (!org.logoUrl && !org.logoId)
    missing.push({
      field: t('dashboard.onboardingCard.missing.logo'),
      icon: 'pi-image',
    });
  if (!org.contacts?.some((c) => c.isPrimary))
    missing.push({
      field: t('dashboard.onboardingCard.missing.contact'),
      icon: 'pi-user',
    });
  if (!org.contacts?.some((c) => c.isPrimary && !!c.phone))
    missing.push({
      field: t('dashboard.onboardingCard.missing.phone'),
      icon: 'pi-phone',
    });
  if (!org.addresses?.some((a) => a.isPrimary && !!a.addressLine1))
    missing.push({
      field: t('dashboard.onboardingCard.missing.address'),
      icon: 'pi-map-marker',
    });
  return missing;
}

const toComplete = computed(() => incompleteOrgs.value.slice(0, 10));
const moreCount = computed(() => Math.max(0, incompleteOrgs.value.length - 10));

// ── Tâches préventives ────────────────────────────────────────────────────────
interface Task {
  id: string;
  orgId: string;
  label: string;
  clientName: string;
  icon: string;
  iconColor: string;
}

const pendingTasks = computed<Task[]>(() => {
  const tasks: Task[] = [];
  const icons = [
    'pi-check-circle',
    'pi-bolt',
    'pi-envelope',
    'pi-refresh',
    'pi-exclamation-triangle',
  ];
  const colors = [
    '#ffd60a',
    '#ef4444',
    'rgba(11,19,32,0.35)',
    '#ffd60a',
    '#ef4444',
  ];
  let idx = 0;
  for (const org of orgs.value) {
    for (const m of getMissingFields(org)) {
      tasks.push({
        id: `${org.id}-${m.field}`,
        orgId: org.id,
        label: m.field,
        clientName: org.name,
        icon: icons[idx % icons.length],
        iconColor: colors[idx % colors.length],
      });
      idx++;
    }
  }
  return tasks.slice(0, 5);
});
</script>

<template>
  <!-- À compléter — white card -->
  <div class="complete-card">
    <div class="complete-card__head">
      <h3 class="complete-card__title">
        {{ t('dashboard.onboardingCard.title') }}
      </h3>
      <span class="complete-card__priority">{{
        t('dashboard.onboardingCard.priority')
      }}</span>
    </div>

    <div v-if="isPending" class="c-skeletons">
      <div v-for="i in 3" :key="i" class="c-skeleton" />
    </div>

    <div v-else-if="toComplete.length > 0" class="complete-list">
      <div
        v-for="org in toComplete"
        :key="org.id"
        class="complete-item"
        @click="router.push(`/clients/${org.id}`)"
      >
        <div class="complete-item__avatar">
          <img v-if="org.logoUrl" :src="org.logoUrl" :alt="org.name" />
          <span v-else>{{ org.name.charAt(0).toUpperCase() }}</span>
        </div>
        <div class="complete-item__body">
          <p class="complete-item__name">{{ org.name }}</p>
          <p class="complete-item__miss">
            <i
              :class="`pi ${getMissingFields(org)[0]?.icon ?? 'pi-exclamation-circle'} complete-item__miss-icon`"
            />
            {{ getMissingFields(org)[0]?.field ?? 'À vérifier' }}
            <span
              v-if="getMissingFields(org).length > 1"
              class="complete-item__miss-count"
            >
              +{{ getMissingFields(org).length - 1 }}
            </span>
          </p>
        </div>
        <i class="pi pi-chevron-right complete-item__arrow" />
      </div>

      <button
        v-if="moreCount > 0"
        class="complete-more-btn"
        @click="router.push('/clients')"
      >
        {{ t('dashboard.onboardingCard.moreClients', { n: moreCount }) }}
      </button>
    </div>

    <div v-else class="c-empty">
      <i class="pi pi-check-circle" style="font-size: 1.5rem; color: #16a34a" />
      <p>{{ t('dashboard.onboarding.allComplete') }}</p>
    </div>
  </div>

  <!-- Préventif — liquid-glass -->
  <div class="preventif-card">
    <div class="preventif-card__head">
      <h3 class="preventif-card__title">
        {{ t('dashboard.onboardingCard.preventive') }}
      </h3>
      <span class="preventif-card__count">{{
        isPending ? '…' : taskCount
      }}</span>
    </div>

    <div v-if="isPending" class="c-skeletons">
      <div v-for="i in 3" :key="i" class="c-skeleton c-skeleton--sm" />
    </div>

    <div v-else-if="pendingTasks.length > 0" class="preventif-list">
      <div
        v-for="task in pendingTasks"
        :key="task.id"
        class="preventif-item"
        @click="router.push(`/clients/${task.orgId}`)"
      >
        <i
          :class="`pi ${task.icon} preventif-item__icon`"
          :style="{ color: task.iconColor }"
        />
        <div class="preventif-item__body">
          <p class="preventif-item__label">{{ task.label }}</p>
          <p class="preventif-item__client">{{ task.clientName }}</p>
        </div>
      </div>
    </div>

    <div v-else class="c-empty">
      <i class="pi pi-check-circle" style="font-size: 1.5rem; color: #16a34a" />
      <p>{{ t('dashboard.onboarding.noTasks') }}</p>
    </div>
  </div>
</template>

<style scoped>
/* ── À compléter — white ── */
.complete-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 2.5rem;
  padding: 2rem;
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
}

.complete-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  gap: 0.75rem;
}
.complete-card__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 800;
  color: #0b1320;
}
.complete-card__priority {
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.2rem 0.5rem;
  border-radius: 0.375rem;
  background: #ef4444;
  color: #fff;
  flex-shrink: 0;
}

.complete-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.complete-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem;
  margin: 0 -0.5rem;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: background 0.12s;
  min-width: 0;
}
.complete-item:hover {
  background: rgba(11, 19, 32, 0.03);
}

.complete-item__left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
}
.complete-item__avatar {
  width: 2.5rem;
  height: 2.5rem;
  min-width: 2.5rem;
  border-radius: 0.75rem;
  background: rgba(11, 19, 32, 0.05);
  color: #0b1320;
  font-size: 0.875rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}
.complete-item__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.complete-item__body {
  flex: 1;
  min-width: 0;
}
.complete-item__name {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 700;
  color: #0b1320;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.complete-item__miss {
  margin: 0.1rem 0 0;
  font-size: 0.6875rem;
  font-weight: 700;
  color: #ef4444;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.complete-item__miss-icon {
  font-size: 0.625rem;
  flex-shrink: 0;
}
.complete-item__miss-count {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
  font-size: 0.5625rem;
  font-weight: 800;
  padding: 0.05rem 0.3rem;
  border-radius: 9999px;
  flex-shrink: 0;
}
.complete-item__arrow {
  font-size: 0.625rem;
  color: rgba(11, 19, 32, 0.2);
  flex-shrink: 0;
}

.complete-more-btn {
  width: 100%;
  margin-top: 1.5rem;
  padding: 0.75rem;
  border: 1.5px dashed #e2e8f0;
  border-radius: 0.75rem;
  background: none;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(11, 19, 32, 0.4);
  cursor: pointer;
  font-family: inherit;
  transition:
    color 0.15s,
    background 0.15s;
}
.complete-more-btn:hover {
  color: #0b1320;
  background: rgba(11, 19, 32, 0.03);
}

/* ── Préventif — liquid-glass ── */
.preventif-card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 8px 32px rgba(11, 19, 32, 0.03);
  border-radius: 2.5rem;
  padding: 2rem;
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
}

.preventif-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  gap: 0.75rem;
}
.preventif-card__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 800;
  color: #0b1320;
}
.preventif-card__count {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  background: #0b1320;
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.preventif-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.preventif-item {
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 1rem;
  padding: 1rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  box-shadow: 0 1px 4px rgba(11, 19, 32, 0.04);
  cursor: pointer;
  transition:
    box-shadow 0.15s,
    transform 0.15s;
  min-width: 0;
}
.preventif-item:hover {
  box-shadow: 0 4px 12px rgba(11, 19, 32, 0.08);
  transform: translateY(-1px);
}
.preventif-item__icon {
  font-size: 1.125rem;
  flex-shrink: 0;
  margin-top: 0.1rem;
}
.preventif-item__body {
  flex: 1;
  min-width: 0;
}
.preventif-item__label {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 700;
  color: #0b1320;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.preventif-item__client {
  margin: 0.175rem 0 0;
  font-size: 0.6875rem;
  color: rgba(11, 19, 32, 0.4);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Skeletons communs */
.c-skeletons {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.c-skeleton {
  height: 3.5rem;
  border-radius: 0.75rem;
  background: linear-gradient(
    90deg,
    rgba(11, 19, 32, 0.04) 25%,
    rgba(11, 19, 32, 0.07) 50%,
    rgba(11, 19, 32, 0.04) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
.c-skeleton--sm {
  height: 2.5rem;
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Vide */
.c-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
  padding: 1.5rem;
  color: rgba(11, 19, 32, 0.35);
  text-align: center;
  font-size: 0.8125rem;
}
.c-empty p {
  margin: 0;
}
</style>

<style>
[data-theme='dark'] .complete-card {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.07);
}
[data-theme='dark'] .complete-card__title {
  color: #f1f5f9;
}
[data-theme='dark'] .complete-item:hover {
  background: rgba(255, 255, 255, 0.04);
}
[data-theme='dark'] .complete-item__name {
  color: #f1f5f9;
}
[data-theme='dark'] .complete-item__avatar {
  background: rgba(255, 255, 255, 0.08);
  color: #f1f5f9;
}
[data-theme='dark'] .complete-more-btn {
  border-color: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.35);
}
[data-theme='dark'] .complete-more-btn:hover {
  color: #f1f5f9;
  background: rgba(255, 255, 255, 0.05);
}

[data-theme='dark'] .preventif-card {
  background: rgba(20, 28, 41, 0.75);
  border-color: rgba(255, 255, 255, 0.07);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}
[data-theme='dark'] .preventif-card__title {
  color: #f1f5f9;
}
[data-theme='dark'] .preventif-card__count {
  background: rgba(255, 255, 255, 0.15);
}
[data-theme='dark'] .preventif-item {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .preventif-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}
[data-theme='dark'] .preventif-item__label {
  color: #f1f5f9;
}
[data-theme='dark'] .preventif-item__client {
  color: rgba(255, 255, 255, 0.3);
}
[data-theme='dark'] .c-skeleton {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.04) 25%,
    rgba(255, 255, 255, 0.07) 50%,
    rgba(255, 255, 255, 0.04) 75%
  );
}
</style>
